import {
  apiAbort,
  requestDelete,
  requestGet,
  requestPatch,
  requestPost,
  requestPut,
} from 'service/api';
import { Api } from 'service/api/constants';
import { call, put, select, takeEvery, takeLeading } from 'typed-redux-saga';
import {
  BlockManagerAddContactBody,
  BlockManagerAddContactRequest,
  BlockManagerArchiveContactBody,
  BlockManagerArchiveContactRequest,
  BlockManagerCancelScheduledVisitRequest,
  BlockManagerDeleteScheduledVisitRequest,
  BlockManagerDownloadFlatsRequest,
  BlockManagerGetFlatAssessmentSummariesRequest,
  BlockManagerGetFlatCommunicationsRequest,
  BlockManagerGetDownloadContactsCSVTemplateRequest,
  BlockManagerGetFlatContactEvacuationInfoRequest,
  BlockManagerGetFlatRequest,
  BlockManagerGetFlatsRequest,
  BlockManagerGetMessagePreviewUrlRequest,
  BlockManagerGetPropertiesRequest,
  BlockManagerGetPropertyAccountsRequest,
  BlockManagerGetPropertySettingsRequest,
  BlockManagerGetScheduledVisitCommunicationScheduleRequest,
  BlockManagerGetScheduledVisitRecipientsRequest,
  BlockManagerGetScheduledVisitsRequest,
  BlockManagerGetScheduledVisitTemplatesRequest,
  GetCommunicationUpdatesRequest,
  BlockManagerListContactsRequest,
  BlockManagerOnboardFlatsRequest,
  BlockManagerPatchPropertySettingsRequest,
  BlockManagerPostScheduledVisitRequest,
  BlockManagerPutFlatContactEvacuationInfoRequest,
  BlockManagerSendCommunicationBulkRequest,
  BlockManagerSendCommunicationRequest,
  BlockManagerSendScheduledVisitMessageNowRequest,
  BlockManagerUpdateContactBody,
  BlockManagerUpdateContactRequest,
  BlockManagerUpdateFlatScheduledVisitAvailabilityBody,
  BlockManagerUpdateFlatScheduledVisitAvailabilityRequest,
  BlockManagerUpdateFlatScheduledVisitAvailabilitySlot,
  BlockManagerUpdateFlatRequest,
  BlockManagerUpdateInspectorsRequest,
  BlockManagerUpdateScheduledVisitRequest,
  BlockManagerUploadFlatContactsRequest,
  EngageContactInteractionType,
  EngageFlatIsCommerciallyOwned,
  EngageFlatIsHMO,
  EngageScheduledVisitStatus,
  ScheduledVisitCommunicationScheduleTemplateType,
} from 'types/DTOs/BlockManagerDTOs';
import {
  doEngageArchiveContact,
  doEngageContactEvacuationInfoLoad,
  doEngageContactEvacuationInfoSave,
  doEngageContactInteractionLogAdd,
  doEngageContactUpdate,
  doEngageDownloadFlatReferences,
  doEngageDownloadTemplate,
  doEngageFlatArchivedContactsLoad,
  doEngageFlatAssessmentSummariesLoad,
  doEngageFlatAssessmentSummariesLoadFailed,
  doEngageFlatAssessmentSummariesSetData,
  doEngageFlatArchivedContactsSet,
  doEngageFlatCommunicationsLoad,
  doEngageFlatCommunicationsLoadFailed,
  doEngageFlatCommunicationsSetData,
  doEngageFlatChangeIsCommerciallyOwned,
  doEngageFlatChangeIsHMO,
  doEngageFlatChangeOccupationStatus,
  doEngageFlatLoad,
  doEngageFlatScheduledVisitAvailabilitySave,
  doEngageFlatScheduledVisitAvailabilitySet,
  doEngageFlatSetData,
  doEngageFlatsLoad,
  doEngageFlatsSetData,
  doEngageFlatUpdateData,
  doEngageOnboard,
  doEngageOpenInspectorSelector,
  doEngagePreviewCommunication,
  doEngagePropertiesLoad,
  doEngagePropertiesSetData,
  doEngagePropertySettingsSave,
  doEngageSaveContact,
  doEngageScheduledVisitCancel,
  doEngageScheduledVisitCommunicationScheduleLoad,
  doEngageScheduledVisitCommunicationScheduleSendNowRequested,
  doEngageScheduledVisitCommunicationScheduleSet,
  doEngageScheduledVisitCommunicationScheduleUnload,
  doEngageScheduledVisitDelete,
  doEngageScheduledVisitMessageSendNow,
  doEngageScheduledVisitRecipientsLoad,
  doEngageScheduledVisitRecipientsSetData,
  doEngageScheduledVisitRecipientsUnload,
  doEngageScheduledVisitSave,
  doEngageScheduledVisitsLoad,
  doEngageScheduledVisitsSetData,
  doEngageScheduledVisitTemplatesLoad,
  doEngageScheduledVisitTemplatesSet,
  doEngageSendCommunication,
  doEngageSendCommunicationBulk,
  doEngageSetLoadState,
  doEngageUnarchiveContact,
  doEngageUploadContacts,
  doEngageViewCommunicationUpdates,
  doOpenPropertySettingsModal,
  eventTypeEngageContactAdded,
  eventTypeScheduledVisitAdded,
} from './engageActions';

import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { differenceInCalendarDays } from 'date-fns';
import { File as ExpoFile } from 'expo-file-system';
import { isError, isNetworkError } from 'lib/isError';
import { pluralise } from 'lib/pluralise';
import {
  dateToIsoString,
  filterArrayEmpty,
  isoDateStringToDate,
  validateEnumFallback,
} from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { FILE_TYPES } from 'service/file/types';
import {
  ALERT,
  ALERT_ERROR,
  doFileShareDirect,
  doToast,
  eventTypeInspectorsSelected,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
} from 'store/actions';
import { selectUserEmail, selectUserName } from 'store/auth/selectors';
import { sagaConfirm, sagaSelect } from 'store/sagaHelpers';
import { NewFlatContactDTO } from 'types/DTOs/FlatContactDTOs';
import {
  CommunicationType,
  EngageContactArchivedReason,
  EngageFlatContact,
  EngageFlatsCommerciallyOwnedFilters,
  EngageFlatsHMOFilters,
  EngageFlatsIssueTypeFilters,
  EngageFlatsOccupationStatusFilters,
  FlatOccupationStatus,
  SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS,
} from 'types/Engage';
import {
  selectEngageFlat,
  selectEngageFlatsFilters,
  selectEngageFlatsPageNumber,
  selectEngagePropertiesFilters,
  selectEngagePropertiesPageNumber,
} from './engageSelectors';

type ArchiveReasonSelection = 'no-longer-associated' | 'mistake' | 'other';

const ARCHIVE_REASON_OPTIONS: {
  internalValue: ArchiveReasonSelection;
  getNameAndValue: (contact: EngageFlatContact) => {
    name: string;
    value: string;
  };
}[] = [
  {
    internalValue: 'no-longer-associated',
    getNameAndValue: (contact) => {
      if (contact.isResident && contact.isOwner) {
        return {
          name: 'No longer owns or lives here',
          value: `${contact.firstName} no longer owns or lives in this flat`,
        };
      }
      if (contact.isResident) {
        return {
          name: 'No longer lives here',
          value: `${contact.firstName} no longer lives in this flat`,
        };
      }
      if (contact.isOwner) {
        return {
          name: 'No longer owns this flat',
          value: `${contact.firstName} no longer owns this flat`,
        };
      }
      return {
        name: 'No longer associated',
        value: `${contact.firstName} is no longer associated with this flat`,
      };
    },
  },
  {
    internalValue: 'mistake',
    getNameAndValue: (contact) => ({
      name: 'Mistake',
      value: `${contact.firstName} was added by mistake`,
    }),
  },
  {
    internalValue: 'other',
    getNameAndValue: () => ({
      name: 'Other',
      value: `Archiving for another reason`,
    }),
  },
];

const resolveArchivedReasonSelection = (
  selection: ArchiveReasonSelection,
  contact: EngageFlatContact,
): EngageContactArchivedReason => {
  if (selection === 'mistake') {
    return EngageContactArchivedReason.mistake;
  }
  if (selection === 'other') {
    return EngageContactArchivedReason.other;
  }
  if (contact.isResident && contact.isOwner) {
    return EngageContactArchivedReason.movedOutStoppedOwning;
  }
  if (contact.isResident) {
    return EngageContactArchivedReason.movedOut;
  }
  if (contact.isOwner) {
    return EngageContactArchivedReason.stoppedOwning;
  }
  return EngageContactArchivedReason.other;
};

export const engageSagas = [
  takeEvery(doEngagePropertiesLoad, function* f(action) {
    const { key, page } = action.payload;
    try {
      const currentPageNumber = yield* select(
        selectEngagePropertiesPageNumber,
        key,
      );
      const newPageNumber = page === 'reset' ? 1 : currentPageNumber + 1;
      const filters = yield* select(selectEngagePropertiesFilters, key);
      const query: BlockManagerGetPropertiesRequest['query'] = {
        page: newPageNumber,
      };
      if (filters.sortBy && filters.sortDirection) {
        query.sortBy = filters.sortBy;
        query.sortDirection = filters.sortDirection;
      }
      const trimmedSearch = filters.search.trim();
      if (trimmedSearch.length) {
        query.search = trimmedSearch;
      }
      const response = yield* requestGet<BlockManagerGetPropertiesRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties',
        params: {},
        query,
      });
      yield* put(
        doEngagePropertiesSetData({
          key,
          response: response,
          clearData: page === 'reset',
          pageNumber: newPageNumber,
        }),
      );
    } catch (error) {
      yield* put(
        doEngageSetLoadState({
          key,
          loading: false,
          section: 'properties',
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageOnboard, function* f(action) {
    const { key, propertyId } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* requestPost<BlockManagerOnboardFlatsRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/onboard',
        params: { propertyUid: propertyId },
        body: {},
      });
      yield* put(HIDE_BLOCKING_LOADER());
      if (response.onboarded || response.updated || response.archived) {
        const reloadConfirm = yield* sagaConfirm({
          title: [
            pluralise(
              response.onboarded,
              '{x} Flat Onboarded',
              '{x} Flats Onboarded',
              '',
            ),
            pluralise(
              response.updated,
              '{x} Flat Updated',
              '{x} Flats Updated',
              '',
            ),
            pluralise(
              response.archived,
              '{x} Flat Archived',
              '{x} Flats Archived',
              '',
            ),
          ]
            .filter(filterArrayEmpty)
            .join(' and '),
          text: `Would you like to reload the list of flats?`,
          confirmText: 'Reload',
          cancelText: smartText("Don't Reload"),
        });
        if (reloadConfirm) {
          yield* put(doEngageFlatsLoad({ key, page: 'reset', propertyId }));
        }
      } else {
        yield* put(
          ALERT({
            title: 'No Flats Onboarded or Updated',
            text: 'Everything is already up-to-date',
          }),
        );
      }
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageFlatsLoad, function* f(action) {
    const { key, page, propertyId } = action.payload;
    try {
      const currentPageNumber = yield* select(selectEngageFlatsPageNumber, key);
      const newPageNumber = page === 'reset' ? 1 : currentPageNumber + 1;
      const filters = yield* select(selectEngageFlatsFilters, key);
      const query: BlockManagerGetFlatsRequest['query'] = {
        page: newPageNumber,
      };
      if (filters.search.trim().length) {
        query.search = filters.search.trim();
      }
      if (
        filters.occupationStatuses.length &&
        filters.occupationStatuses.length <
          EngageFlatsOccupationStatusFilters.length
      ) {
        query.occupationStatuses = filters.occupationStatuses;
      }
      if (
        filters.issueContactTypes.length &&
        filters.issueContactTypes.length <= EngageFlatsIssueTypeFilters.length
      ) {
        query.issueContactTypes = filters.issueContactTypes;
      }
      if (filters.requiresResidentSupport) {
        query.requiresResidentSupport = filters.requiresResidentSupport;
      }
      if (
        filters.isHMO.length &&
        filters.isHMO.length < EngageFlatsHMOFilters.length
      ) {
        query.isHMO = filters.isHMO;
      }
      if (
        filters.isCommerciallyOwned.length &&
        filters.isCommerciallyOwned.length <
          EngageFlatsCommerciallyOwnedFilters.length
      ) {
        query.isCommerciallyOwned = filters.isCommerciallyOwned;
      }
      const response = yield* requestGet<BlockManagerGetFlatsRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats',
        params: { propertyUid: propertyId },
        query,
      });
      yield* put(
        doEngageFlatsSetData({
          key,
          response: response,
          clearData: page === 'reset',
          pageNumber: newPageNumber,
        }),
      );
    } catch (error) {
      yield* put(
        doEngageSetLoadState({
          key,
          loading: false,
          section: 'flats',
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageFlatLoad, function* f(action) {
    const { key, property, flat } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* requestGet<BlockManagerGetFlatRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/:flatUid',
        params: {
          flatUid: flat.id,
          propertyUid: property.id,
        },
      });
      yield* put(doEngageFlatSetData({ key, property, flat, response }));
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngageFlat,
          params: {
            key: key,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeLeading(doEngageFlatCommunicationsLoad, function* f(action) {
    const { key, propertyId, flatId, page } = action.payload;
    try {
      const currentFlat = yield* select(selectEngageFlat, key);
      if (
        !currentFlat ||
        currentFlat.flatDetails.id !== flatId ||
        currentFlat.property.id !== propertyId
      ) {
        return;
      }
      const currentPageNumber = currentFlat.communicationsPageNumber;
      const newPageNumber = page === 'reset' ? 1 : currentPageNumber + 1;
      const response =
        yield* requestGet<BlockManagerGetFlatCommunicationsRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications',
          params: {
            propertyUid: propertyId,
            flatUid: flatId,
          },
          query: {
            page: newPageNumber,
          },
        });
      yield* put(
        doEngageFlatCommunicationsSetData({
          key,
          propertyId,
          flatId,
          response,
          clearData: page === 'reset',
          pageNumber: newPageNumber,
        }),
      );
    } catch (error) {
      yield* put(
        doEngageFlatCommunicationsLoadFailed({
          key,
          propertyId,
          flatId,
          page,
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeLeading(doEngageFlatAssessmentSummariesLoad, function* f(action) {
    const { key, propertyId, flatId, page } = action.payload;
    try {
      const currentFlat = yield* select(selectEngageFlat, key);
      if (
        !currentFlat ||
        currentFlat.flatDetails.id !== flatId ||
        currentFlat.property.id !== propertyId
      ) {
        return;
      }
      const currentPageNumber = currentFlat.assessmentSummariesPageNumber;
      const newPageNumber = page === 'reset' ? 1 : currentPageNumber + 1;
      const response =
        yield* requestGet<BlockManagerGetFlatAssessmentSummariesRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/assessment-summaries',
          params: {
            propertyUid: propertyId,
            flatUid: flatId,
          },
          query: {
            page: newPageNumber,
          },
        });
      yield* put(
        doEngageFlatAssessmentSummariesSetData({
          key,
          propertyId,
          flatId,
          response,
          clearData: page === 'reset',
          pageNumber: newPageNumber,
        }),
      );
    } catch (error) {
      yield* put(
        doEngageFlatAssessmentSummariesLoadFailed({
          key,
          propertyId,
          flatId,
          page,
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doOpenPropertySettingsModal, function* f(action) {
    const { propertyId, pageKey } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response =
        yield* requestGet<BlockManagerGetPropertySettingsRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/settings',
          params: {
            propertyUid: propertyId,
          },
        });
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngagePropertySettings,
          size: 'small',
          params: {
            propertyId,
            pageKey,
            settings: response,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngagePropertySettingsSave, function* f(action) {
    const { propertyId, body } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestPatch<BlockManagerPatchPropertySettingsRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/settings',
        params: {
          propertyUid: propertyId,
        },
        body,
      });
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.EngagePropertySettings]));
      yield* put(doToast('Property settings saved'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageFlatArchivedContactsLoad, function* f(action) {
    const { key, propertyId, flatId } = action.payload;
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response = yield* requestGet<BlockManagerListContactsRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts',
        params: {
          propertyUid: propertyId,
          flatUid: flatId,
        },
        query: {
          state: 'archived',
        },
      });
      yield* put(
        doEngageFlatArchivedContactsSet({
          key,
          contacts: response.contacts,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageFlatChangeOccupationStatus, function* f(action) {
    const { key } = action.payload;
    const flat = yield* select(selectEngageFlat, key);
    if (!flat) {
      return;
    }
    const ownerResidents = flat.contacts.filter(
      (contact) => contact.isResident && contact.isOwner,
    );
    const residents = flat.contacts.filter(
      (contact) => contact.isResident && !contact.isOwner,
    );
    yield* sagaSelect({
      selectConfig: {
        title: 'Change Occupation Status',
        value: [flat.flatDetails.occupationStatus],
        hideRadioButton: true,
        showDoneButtonForSingleSelect: false,
        options: [
          {
            name: 'Occupied',
            value: 'You know somebody lives here',
            internalValue: FlatOccupationStatus.OCCUPIED,
          },
          {
            name: 'Unoccupied',
            value: 'You know nobody lives here',
            internalValue: FlatOccupationStatus.UNOCCUPIED,
          },
          {
            name: 'Not Known',
            value: 'You don’t know if anyone lives here',
            internalValue: FlatOccupationStatus.UNKNOWN,
          },
        ].map((item) => ({
          name: item.name,
          value: item.value,
          labelAlignTop: true,
          internalValue: item.internalValue,
          icon:
            flat.flatDetails.occupationStatus === item.internalValue
              ? ICONS.CircleDotSolid
              : ICONS.CircleLight,
          iconColor:
            flat.flatDetails.occupationStatus === item.internalValue
              ? theme.colors.blue
              : undefined,
        })),
      },
      onSelect: function* (values) {
        let doApiCall = true;
        const occupationStatus = validateEnumFallback(
          FlatOccupationStatus,
          values[0],
          FlatOccupationStatus.OCCUPIED,
        );
        if (
          (occupationStatus === FlatOccupationStatus.UNOCCUPIED ||
            occupationStatus === FlatOccupationStatus.UNKNOWN) &&
          (ownerResidents.length || residents.length)
        ) {
          // Build confirmation message
          const confirmMessageOwnerResidentToOwner = pluralise(
            ownerResidents.length,
            '{x} owner-resident will become an owner only',
            '{x} owner-residents will become owners only',
          );
          let confirmMessage =
            `Are you sure you want to change this flat’s status to ***` +
            `${
              occupationStatus === FlatOccupationStatus.UNOCCUPIED
                ? 'unoccupied'
                : 'unknown'
            }***` +
            `?\n\n`;
          if (residents.length) {
            confirmMessage +=
              pluralise(
                residents,
                '{x} resident will be archived',
                '{x} residents will be archived',
              ) +
              (ownerResidents.length
                ? ` and ${confirmMessageOwnerResidentToOwner}`
                : '');
          } else {
            confirmMessage += confirmMessageOwnerResidentToOwner;
          }
          // Confirm with user if they want to archive residents
          doApiCall =
            (yield* sagaConfirm({
              title: `Change Occupation Status`,
              text: confirmMessage,
              destructive: true,
              confirmText: 'Change',
            })) === true;
        }
        if (!doApiCall) {
          return false;
        }
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response = yield* requestPut<BlockManagerUpdateFlatRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/flats/:flatUid',
            params: {
              propertyUid: flat.property.id,
              flatUid: flat.flatDetails.id,
            },
            body: {
              occupationStatus,
            },
          });
          yield* put(
            doEngageFlatUpdateData({
              key,
              occupationStatus,
              archivedContactIds: response.archivedContactIds,
              contacts: response.updatedContacts,
              issues: response.issues,
            }),
          );
          yield* put(HIDE_BLOCKING_LOADER());
          return true;
        } catch (error) {
          yield* put(HIDE_BLOCKING_LOADER());
          if (isError(error)) {
            yield* put(ALERT_ERROR({ error }));
          }
          return false;
        }
      },
    });
  }),

  takeEvery(doEngageUploadContacts, function* f(action) {
    const { key, propertyId, file, fileName } = action.payload;

    try {
      yield* put(SHOW_BLOCKING_LOADER());

      const fileUploadForm = new FormData();

      if (file.file) {
        // Web platform - use the File object directly
        fileUploadForm.append('file', file.file, fileName);
      } else if (file.uri) {
        // React Native - read file content and create proper blob
        const bufferData = yield* call([new ExpoFile(file.uri), 'arrayBuffer']);

        const blob = new Blob([bufferData], { type: 'text/csv' });

        fileUploadForm.append('file', blob, fileName);
      } else {
        throw new Error('No file or URI provided');
      }

      yield* requestPost<BlockManagerUploadFlatContactsRequest>(
        {
          api: Api.NODE,
          url: `/blockmanagement/properties/:propertyUid/contacts/upload`,
          params: { propertyUid: propertyId },
          body: fileUploadForm,
        },
        {
          multiformData: true,
          fileName: fileName,
        },
      );

      // Upload successful
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.EngageContactUpload]));
      yield* put(doEngageFlatsLoad({ key, page: 'reset', propertyId }));
      yield* put(doToast('Contacts uploaded successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageDownloadFlatReferences, function* f(action) {
    const { propertyId } = action.payload;

    // show blocking loader for download
    try {
      const response = yield* requestGet<BlockManagerDownloadFlatsRequest>({
        api: Api.NODE,
        url: `/blockmanagement/properties/:propertyUid/export-flats`,
        params: { propertyUid: propertyId },
      });

      // hide blocking loader
      yield* put(
        doFileShareDirect({
          url: response.url,
          title: response.fileName,
          fileType: FILE_TYPES.csv,
        }),
      );

      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(doToast('Flat references downloaded successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageDownloadTemplate, function* f(_action) {
    // show blocking loader for download
    try {
      const response =
        yield* requestGet<BlockManagerGetDownloadContactsCSVTemplateRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/contacts-import-template',
          params: {},
        });

      // hide blocking loader
      yield* put(
        doFileShareDirect({
          url: response.url,
          title: response.fileName,
          fileType: FILE_TYPES.csv,
        }),
      );

      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(doToast('Contacts import template downloaded successfully'));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageFlatChangeIsHMO, function* f(action) {
    const { key } = action.payload;
    const flat = yield* select(selectEngageFlat, key);
    if (!flat) {
      return;
    }

    yield* sagaSelect({
      selectConfig: {
        title: 'HMO',
        text: 'Is this a House in Multiple Occupation?',
        value: [flat.flatDetails.isHMO],
        hideRadioButton: true,
        showDoneButtonForSingleSelect: false,
        options: [
          {
            name: 'Yes',
            value: 'This flat is a HMO',
            internalValue: EngageFlatIsHMO.YES,
          },
          {
            name: 'No',
            value: 'This flat is not a HMO',
            internalValue: EngageFlatIsHMO.NO,
          },
          {
            name: 'Don’t Know',
            value: 'You don’t know if this flat is a HMO',
            internalValue: EngageFlatIsHMO.UNKNOWN,
          },
        ].map((item) => ({
          name: item.name,
          value: item.value,
          labelAlignTop: true,
          internalValue: item.internalValue,
          icon:
            flat.flatDetails.isHMO === item.internalValue
              ? ICONS.CircleDotSolid
              : ICONS.CircleLight,
          iconColor:
            flat.flatDetails.isHMO === item.internalValue
              ? theme.colors.blue
              : undefined,
        })),
      },
      onSelect: function* (values) {
        const isHMO = validateEnumFallback(
          EngageFlatIsHMO,
          values[0],
          EngageFlatIsHMO.YES,
        );
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response = yield* requestPut<BlockManagerUpdateFlatRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/flats/:flatUid',
            params: {
              propertyUid: flat.property.id,
              flatUid: flat.flatDetails.id,
            },
            body: {
              isHMO: isHMO,
            },
          });
          yield* put(
            doEngageFlatUpdateData({
              key,
              isHMO: response.isHMO,
              issues: response.issues,
            }),
          );
          yield* put(HIDE_BLOCKING_LOADER());
          return true;
        } catch (error) {
          yield* put(HIDE_BLOCKING_LOADER());
          if (isError(error)) {
            yield* put(ALERT_ERROR({ error }));
          }
          return false;
        }
      },
    });
  }),

  takeEvery(doEngageFlatChangeIsCommerciallyOwned, function* f(action) {
    const { key } = action.payload;
    const flat = yield* select(selectEngageFlat, key);
    if (!flat) {
      return;
    }

    yield* sagaSelect({
      selectConfig: {
        title: 'Institutional Landlord',
        text: 'Does this flat have an institutional landlord?',
        titleNumberOfLines: 2,
        value: [flat.flatDetails.isCommerciallyOwned],
        hideRadioButton: true,
        showDoneButtonForSingleSelect: false,
        options: [
          {
            name: 'Yes',
            value: 'This flat has an institutional landlord',
            internalValue: EngageFlatIsCommerciallyOwned.YES,
          },
          {
            name: 'No',
            value: 'This flat does not have an institutional landlord',
            internalValue: EngageFlatIsCommerciallyOwned.NO,
          },
          {
            name: 'Don’t Know',
            value: 'You don’t know if this flat has an institutional landlord',
            internalValue: EngageFlatIsCommerciallyOwned.UNKNOWN,
          },
        ].map((item) => ({
          name: item.name,
          value: item.value,
          labelAlignTop: true,
          internalValue: item.internalValue,
          icon:
            flat.flatDetails.isCommerciallyOwned === item.internalValue
              ? ICONS.CircleDotSolid
              : ICONS.CircleLight,
          iconColor:
            flat.flatDetails.isCommerciallyOwned === item.internalValue
              ? theme.colors.blue
              : undefined,
        })),
      },
      onSelect: function* (values) {
        const isCommerciallyOwned = validateEnumFallback(
          EngageFlatIsCommerciallyOwned,
          values[0],
          EngageFlatIsCommerciallyOwned.YES,
        );
        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response = yield* requestPut<BlockManagerUpdateFlatRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/flats/:flatUid',
            params: {
              propertyUid: flat.property.id,
              flatUid: flat.flatDetails.id,
            },
            body: {
              isCommerciallyOwned: isCommerciallyOwned,
            },
          });
          yield* put(
            doEngageFlatUpdateData({
              key,
              isCommerciallyOwned: response.isCommerciallyOwned,
              issues: response.issues,
            }),
          );
          yield* put(HIDE_BLOCKING_LOADER());
          return true;
        } catch (error) {
          yield* put(HIDE_BLOCKING_LOADER());
          if (isError(error)) {
            yield* put(ALERT_ERROR({ error }));
          }
          return false;
        }
      },
    });
  }),

  takeEvery(doEngageFlatScheduledVisitAvailabilitySave, function* f(action) {
    const { key, propertyId, flatId, scheduledVisitId, isUnavailable, slots } =
      action.payload;
    const availability = { isUnavailable, slots };

    const confirmed = yield* sagaConfirm({
      title: 'Modify Door Check Availability',
      text: 'Are you sure you want to modify this door check availability?',
      confirmText: 'Modify',
    });
    if (!confirmed) {
      return;
    }

    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestPut<BlockManagerUpdateFlatScheduledVisitAvailabilityRequest>(
        {
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/scheduled-visits/:scheduledVisitId',
          params: {
            propertyUid: propertyId,
            flatUid: flatId,
            scheduledVisitId,
          },
          body: new BlockManagerUpdateFlatScheduledVisitAvailabilityBody({
            isUnavailable: availability.isUnavailable,
            slots: availability.slots.map(
              (slot) =>
                new BlockManagerUpdateFlatScheduledVisitAvailabilitySlot(slot),
            ),
          }),
        },
      );

      yield* put(
        doEngageFlatScheduledVisitAvailabilitySet({
          key,
          flatId,
          scheduledVisitId,
          ...availability,
        }),
      );
      yield* put(
        NAVIGATE_MODAL_BACK([ModalScreens.EngageFlatScheduledVisitData]),
      );
      yield* put(doToast('Scheduled visit response updated'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  /**
   * Contacts
   */
  takeEvery(doEngageArchiveContact, function* f(action) {
    const { key, propertyId, flatId, contactId } = action.payload;

    // get contact for modal title
    const flatData = yield* select(selectEngageFlat, key);
    if (!flatData) {
      return;
    }
    const contact = flatData.contacts.find((c) => c.id === contactId);
    if (!contact) {
      return;
    }
    const fullName = `${contact.firstName} ${contact.lastName}`.trim();

    yield* sagaSelect({
      selectConfig: {
        title: 'Archive Contact',
        subtitle: fullName,
        text: `This will remove ${contact.firstName} as an active contact but their details will be available in Archived Contacts`,
        value: [],
        showDoneButtonForSingleSelect: false,
        submitOnSelection: true,
        requireValue: true,
        hideRadioButton: true,
        options: ARCHIVE_REASON_OPTIONS.map((option) => ({
          ...option.getNameAndValue(contact),
          internalValue: option.internalValue,
          labelAlignTop: true,
        })),
      },
      onSelect: function* (values) {
        const selectedReason = values[0] as ArchiveReasonSelection | undefined;
        if (!selectedReason) {
          return false;
        }

        yield* put(SHOW_BLOCKING_LOADER());
        try {
          const response =
            yield* requestPost<BlockManagerArchiveContactRequest>({
              api: Api.NODE,
              url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/:action',
              params: {
                propertyUid: propertyId,
                flatUid: flatId,
                contactUid: contactId,
                action: 'archive',
              },
              body: {
                archivedReason: resolveArchivedReasonSelection(
                  selectedReason,
                  contact,
                ),
              },
            });
          yield* put(
            doEngageFlatUpdateData({
              key,
              archivedContactIds: [contactId],
              occupationStatus: response.flatOccupationStatus,
              issues: response.issues,
            }),
          );
          yield* put(HIDE_BLOCKING_LOADER());
          yield* put(doToast('Contact Archived'));
          return true;
        } catch (error) {
          yield* put(HIDE_BLOCKING_LOADER());
          if (isError(error)) {
            yield* put(ALERT_ERROR({ error }));
          }
          return false;
        }
      },
    });
  }),

  takeEvery(doEngageUnarchiveContact, function* f(action) {
    const { key, propertyId, flatId, contactId } = action.payload;
    const flatData = yield* select(selectEngageFlat, key);
    if (!flatData) {
      return;
    }
    const contact =
      flatData.archivedContacts?.find((c) => c.id === contactId) ?? null;
    if (!contact) {
      return;
    }
    const fullName =
      `${contact.firstName} ${contact.lastName}`.trim() || 'this contact';

    const confirmed = yield* sagaConfirm({
      title: `Unarchive ${fullName}?`,
      confirmText: 'Unarchive',
      text: 'This will restore the contact to the active contacts list.',
    });

    if (!confirmed) {
      return;
    }

    try {
      const response = yield* requestPost<BlockManagerArchiveContactRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/:action',
        params: {
          propertyUid: propertyId,
          flatUid: flatId,
          contactUid: contactId,
          action: 'unarchive',
        },
        body: new BlockManagerArchiveContactBody({}),
      });

      yield* put(
        doEngageFlatUpdateData({
          key,
          unarchivedContactIds: [contactId],
          occupationStatus: response.flatOccupationStatus,
          issues: response.issues,
        }),
      );
      yield* put(
        doEngageContactUpdate({
          key,
          isNew: false,
          propertyId,
          flatId,
          contact: response.contact!,
        }),
      );

      yield* put(doToast('Contact Unarchived'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageSaveContact, function* f(action) {
    const { key, isNew, flatId, propertyId, contact, actionAfterSave } =
      action.payload;

    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = isNew
        ? yield* requestPost<BlockManagerAddContactRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contact',
            params: {
              propertyUid: propertyId,
              flatUid: flatId,
            },
            body: new BlockManagerAddContactBody({
              contact: new NewFlatContactDTO(contact),
            }),
          })
        : yield* requestPut<BlockManagerUpdateContactRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid',
            params: {
              propertyUid: propertyId,
              flatUid: flatId,
              contactUid: contact.id,
            },
            body: new BlockManagerUpdateContactBody({
              contact: new NewFlatContactDTO(contact),
            }),
          });
      if (isNew) {
        yield* put(
          eventTypeEngageContactAdded({ contactId: response.contact.id }),
        );
      }
      yield* put(
        doEngageContactUpdate({
          key,
          isNew,
          propertyId: propertyId,
          flatId: flatId,
          contact: response.contact,
        }),
      );
      yield* put(
        doEngageFlatUpdateData({
          key,
          occupationStatus: response.flatOccupationStatus,
          issues: response.issues,
        }),
      );
      if (actionAfterSave === 'openEmergencyEvacuationDetails') {
        yield* put(
          doEngageContactEvacuationInfoLoad({
            key,
            propertyId,
            flatId,
            contactId: response.contact.id,
            contactName:
              `${response.contact.firstName} ${response.contact.lastName}`.trim() ||
              'Contact',
          }),
        );
      } else {
        yield* put(NAVIGATE_MODAL_BACK([ModalScreens.EngageContact]));
      }
      yield* put(doToast(isNew ? 'Contact Created' : 'Contact Updated'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageContactEvacuationInfoLoad, function* f(action) {
    const { key, propertyId, flatId, contactId, contactName } = action.payload;

    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response =
        yield* requestGet<BlockManagerGetFlatContactEvacuationInfoRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/evacuation-info',
          params: {
            propertyUid: propertyId,
            flatUid: flatId,
            contactUid: contactId,
          },
        });
      // This is purely for the reducer - allowing visual updates while within the contact modal.
      const userName = (yield* select(selectUserName)) ?? 'Unknown User';
      const userEmail = (yield* select(selectUserEmail)) ?? 'email@example.com';
      yield* put(
        doEngageContactInteractionLogAdd({
          key,
          flatId,
          contactId,
          log: {
            interactionType:
              EngageContactInteractionType.flatContactViewEvacuationInfo,
            name: userName,
            email: userEmail,
            createdDate: dateToIsoString(new Date()),
          },
        }),
      );

      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngageContactEmergencyEvacuation,
          params: {
            key: key,
            contactName,
            contactId,
            flatId,
            propertyId,
            data: response,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }

    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageContactEvacuationInfoSave, function* f(action) {
    const { key, propertyId, flatId, contactId, info } = action.payload;

    yield* put(SHOW_BLOCKING_LOADER());

    try {
      const trimmedOtherSupportNeed =
        typeof info.otherSupportNeed === 'string'
          ? info.otherSupportNeed.trim()
          : null;

      yield* requestPut<BlockManagerPutFlatContactEvacuationInfoRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/contacts/:contactUid/evacuation-info',
        params: {
          propertyUid: propertyId,
          flatUid: flatId,
          contactUid: contactId,
        },
        body: {
          hasSupportNeeds: info.hasSupportNeeds,
          hasMobilityLimitation: info.hasMobilityLimitation,
          hasCognitiveImpairment: info.hasCognitiveImpairment,
          hasSocialAnxiety: info.hasSocialAnxiety,
          hasHearingImpairment: info.hasHearingImpairment,
          hasVisualImpairment: info.hasVisualImpairment,
          hasOtherSupportNeed: info.hasOtherSupportNeed,
          otherSupportNeed: info.hasOtherSupportNeed
            ? trimmedOtherSupportNeed || null
            : null,
          emergencyEvacuationSharingStatus:
            info.emergencyEvacuationSharingStatus,
          emergencyEvacuationStatement: info.emergencyEvacuationStatement ?? '',
          emergencyEvacuationStatementAgreedDate:
            info.emergencyEvacuationStatementAgreedDate,
          emergencyEvacuationSharingAgreedDate:
            info.emergencyEvacuationSharingAgreedDate,
          emergencyEvacuationAssistanceSummary:
            info.emergencyEvacuationAssistanceSummary ?? '',
        },
      });

      // Update contact's `hasSupportNeeds`
      const flat = yield* select(selectEngageFlat, key);
      const contact = flat?.contacts.find((c) => c.id === contactId);
      if (contact) {
        yield* put(
          doEngageContactUpdate({
            key,
            flatId,
            propertyId,
            isNew: false,
            contact: { ...contact, hasSupportNeeds: info.hasSupportNeeds },
          }),
        );
      }

      // This is purely for the reducer - allowing visual updates while within the contact modal.
      const userName = (yield* select(selectUserName)) ?? 'Unknown User';
      const userEmail = (yield* select(selectUserEmail)) ?? 'email@example.com';
      yield* put(
        doEngageContactInteractionLogAdd({
          key,
          flatId,
          contactId,
          log: {
            interactionType:
              EngageContactInteractionType.flatContactEditEvacuationInfo,
            name: userName,
            email: userEmail,
            createdDate: dateToIsoString(new Date()),
          },
        }),
      );

      // Close modal
      yield* put(
        NAVIGATE_MODAL_BACK([ModalScreens.EngageContactEmergencyEvacuation]),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }

    yield* put(HIDE_BLOCKING_LOADER());
  }),

  /**
   * Communications
   */

  takeEvery(doEngageSendCommunication, function* f(action) {
    const { key, propertyId, flatId, communicationId, contactIds } =
      action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestPost<BlockManagerSendCommunicationRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/sendcommunication',
        params: { propertyUid: propertyId, flatUid: flatId },
        body: {
          communicationTemplateId: communicationId,
          contactIds: contactIds,
        },
      });
      const currentFlat = yield* select(selectEngageFlat, key);
      if (currentFlat?.flatDetails.id === flatId) {
        yield* put(
          doEngageFlatCommunicationsLoad({
            key,
            propertyId,
            flatId,
            page: 'reset',
          }),
        );
      }
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.EngageSendCommunication]));
      yield* put(doToast('Message Sent'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngagePreviewCommunication, function* f(action) {
    const {
      propertyId,
      flatId,
      communicationId,
      communicationName,
      communicationType,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response =
        yield* requestGet<BlockManagerGetMessagePreviewUrlRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/flats/:flatUid/communications/:communicationUid/previewurl',
          params: {
            propertyUid: propertyId,
            flatUid: flatId,
            communicationUid: communicationId,
          },
        });
      yield* put(
        NAVIGATE_MODAL({
          screen:
            communicationType === CommunicationType.email
              ? ModalScreens.WEBVIEWER
              : ModalScreens.PDFVIEWER,
          params: {
            title: communicationName,
            sourceUrl: response.url,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageViewCommunicationUpdates, function* f(action) {
    const { communicationId, communicationName } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      const response = yield* requestGet<GetCommunicationUpdatesRequest>({
        api: Api.NODE,
        url: '/blockmanagement/communications/:communicationUid/updates',
        params: {
          communicationUid: communicationId,
        },
      });
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.EngageCommunicationUpdates,
          params: {
            communicationId,
            communicationName,
            communicationUpdates: response.communicationUpdates,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageSendCommunicationBulk, function* f(action) {
    const {
      propertyId,
      flatIds,
      communicationId,
      owners,
      residents,
      primaryContactsOnly,
    } = action.payload;
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestPost<BlockManagerSendCommunicationBulkRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/sendcommunicationbulk',
        params: { propertyUid: propertyId },
        body: {
          communicationTemplateId: communicationId,
          flatIds: flatIds,
          owners,
          residents,
          primaryContactsOnly,
        },
      });
      yield* put(NAVIGATE_MODAL_BACK([ModalScreens.EngageSendCommunication]));
      yield* put(doToast('Message Sent'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageScheduledVisitSave, function* f(action) {
    const {
      propertyId,
      timeslots,
      scheduledVisitId,
      status,
      inspectorIds,
      currentStatus,
      pageKey,
    } = action.payload;
    const canUpdateDetails = currentStatus === EngageScheduledVisitStatus.DRAFT;
    const isPublishing =
      currentStatus === EngageScheduledVisitStatus.DRAFT &&
      status === EngageScheduledVisitStatus.PUBLISHED;
    try {
      // If publishing, make the user confirm
      if (isPublishing) {
        const lessThanRequestSafeDaysUntilStart = timeslots.some(
          (slot) =>
            differenceInCalendarDays(
              isoDateStringToDate(slot.date),
              new Date(),
            ) < SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS,
        );
        const confirmed = yield* sagaConfirm({
          title: 'Publish Scheduled Visit',
          text:
            (lessThanRequestSafeDaysUntilStart
              ? `This scheduled visit starts in less than ${SCHEDULED_VISIT_COMMUNICATION_REQUEST_DAYS} days. ` +
                `This may result in lower availability of residents and RiskBase may send multiple reminder emails at once.\n\n`
              : '') +
            'Once this visit is published, it cannot be edited. You can only cancel it.',
          confirmText: 'Publish',
        });
        if (!confirmed) {
          return;
        }
      }
      yield* put(SHOW_BLOCKING_LOADER());
      if (!scheduledVisitId) {
        /**
         * CREATE
         */
        const { scheduledVisitId: createdScheduledVisitId } =
          yield* requestPost<BlockManagerPostScheduledVisitRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/scheduled-visit',
            params: { propertyUid: propertyId },
            body: { timeslots, status: EngageScheduledVisitStatus.DRAFT },
          });
        yield* put(
          eventTypeScheduledVisitAdded({
            scheduledVisitId: createdScheduledVisitId,
          }),
        );
        // Add inspectors
        if (inspectorIds.length) {
          yield* requestPost<BlockManagerUpdateInspectorsRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/inspectors',
            params: {
              propertyUid: propertyId,
              scheduledVisitId: createdScheduledVisitId,
            },
            body: { inspectorIds },
          });
        }
        // Publish new scheduled visit
        if (isPublishing) {
          yield* requestPut<BlockManagerUpdateScheduledVisitRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId',
            params: {
              propertyUid: propertyId,
              scheduledVisitId: createdScheduledVisitId,
            },
            body: { timeslots, status },
          });
        }
      } else {
        /**
         * UPDATE
         */
        // Update inspectors
        yield* requestPost<BlockManagerUpdateInspectorsRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/inspectors',
          params: {
            propertyUid: propertyId,
            scheduledVisitId,
          },
          body: { inspectorIds },
        });
        // Update details
        if (canUpdateDetails) {
          yield* requestPut<BlockManagerUpdateScheduledVisitRequest>({
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId',
            params: {
              propertyUid: propertyId,
              scheduledVisitId,
            },
            body: { timeslots, status },
          });
        }
      }
      yield* put(
        doEngageScheduledVisitsLoad({
          key: pageKey,
          propertyId: propertyId,
        }),
      );
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(
        doToast(
          isPublishing
            ? 'Scheduled Visit Published'
            : 'Scheduled Visit Updated',
        ),
      );
      yield* put(HIDE_BLOCKING_LOADER());
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      // Load even if error occured
      yield* put(
        doEngageScheduledVisitsLoad({
          key: pageKey,
          propertyId: propertyId,
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageScheduledVisitDelete, function* f(action) {
    const { propertyId, scheduledVisitId, pageKey } = action.payload;
    const confirmed = yield* sagaConfirm({
      title: 'Delete Draft Scheduled Visit',
      text: 'Once this draft is deleted, it cannot be recovered.',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestDelete<BlockManagerDeleteScheduledVisitRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId',
        params: { propertyUid: propertyId, scheduledVisitId },
      });
      yield* put(
        doEngageScheduledVisitsLoad({
          key: pageKey,
          propertyId,
        }),
      );
      yield* put(doToast('Scheduled Visit Draft Deleted'));
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageScheduledVisitCancel, function* f(action) {
    const { propertyId, scheduledVisitId, cancelledReason, pageKey } =
      action.payload;

    try {
      const reloadConfirm = yield* sagaConfirm({
        title: 'Cancel Scheduled Visit',
        text: 'Cancelling this scheduled visit is a non-recoverable action',
        confirmText: 'Confirm',
        cancelText: smartText("Don't Cancel"),
        destructive: true,
      });
      if (!reloadConfirm) {
        return;
      }
      yield* put(SHOW_BLOCKING_LOADER());
      yield* requestPost<BlockManagerCancelScheduledVisitRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/cancel',
        params: { propertyUid: propertyId, scheduledVisitId },
        body: { cancelledReason },
      });
      yield* put(
        doEngageScheduledVisitsLoad({
          key: pageKey,
          propertyId,
        }),
      );
      yield* put(doToast('Scheduled Visit Cancelled'));
      yield* put(NAVIGATE_MODAL_BACK());
      yield* put(NAVIGATE_MODAL_BACK());
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageScheduledVisitMessageSendNow, function* f(action) {
    const { propertyId, scheduledVisitId, pageKey, templateType } =
      action.payload;
    const isReminderOrChaser =
      templateType ===
        ScheduledVisitCommunicationScheduleTemplateType.REMINDER ||
      templateType === ScheduledVisitCommunicationScheduleTemplateType.CHASER;

    const confirmed = yield* sagaConfirm({
      title: 'Send Scheduled Visit Message',
      text: isReminderOrChaser
        ? 'Reminder and Chaser messages must be sent at the same time. Are you sure you want to sending the reminder and chaser message now?'
        : 'Are you sure you want to send this scheduled visit message immediately?',
      confirmText: 'Send now',
    });
    if (!confirmed) {
      return;
    }

    yield* put(SHOW_BLOCKING_LOADER());
    try {
      yield* requestPost<BlockManagerSendScheduledVisitMessageNowRequest>({
        api: Api.NODE,
        url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/send-now',
        params: { propertyUid: propertyId, scheduledVisitId },
        body: { templateType },
      });
      yield* put(
        doEngageScheduledVisitCommunicationScheduleSendNowRequested({
          key: pageKey,
          templateType,
          sendNowRequestedDate: dateToIsoString(new Date()),
        }),
      );
      yield* put(
        doEngageScheduledVisitCommunicationScheduleLoad({
          key: pageKey,
          propertyUid: propertyId,
          scheduledVisitId,
        }),
      );
      yield* put(
        doEngageScheduledVisitsLoad({
          key: pageKey,
          propertyId,
        }),
      );
      yield* put(doToast('Message Sent'));
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
    yield* put(HIDE_BLOCKING_LOADER());
  }),

  takeEvery(doEngageScheduledVisitsLoad, function* f(action) {
    const { key, propertyId } = action.payload;
    try {
      const response = yield* requestGet<BlockManagerGetScheduledVisitsRequest>(
        {
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/scheduled-visits',
          params: { propertyUid: propertyId },
          query: {},
        },
      );

      yield* put(
        doEngageScheduledVisitsSetData({
          key,
          response,
        }),
      );
    } catch (error) {
      yield* put(
        doEngageSetLoadState({
          key,
          loading: false,
          section: 'scheduledVisits',
        }),
      );
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageScheduledVisitRecipientsLoad, function* f(action) {
    const { key, propertyId } = action.payload;
    try {
      const response =
        yield* requestGet<BlockManagerGetScheduledVisitRecipientsRequest>(
          {
            api: Api.NODE,
            url: '/blockmanagement/properties/:propertyUid/scheduled-visit-recipients',
            params: { propertyUid: propertyId },
          },
          { abortKey: 'scheduledVisitRecipientsLoad' },
        );

      yield* put(
        doEngageScheduledVisitRecipientsSetData({
          key,
          data: response,
        }),
      );
    } catch (error) {
      if (isError(error) && (!isNetworkError(error) || !error.isCancelled)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),

  takeEvery(doEngageScheduledVisitRecipientsUnload, function* f() {
    yield* apiAbort('scheduledVisitRecipientsLoad');
  }),

  takeEvery(doEngageScheduledVisitTemplatesLoad, function* f(action) {
    const { key } = action.payload;
    try {
      const response =
        yield* requestGet<BlockManagerGetScheduledVisitTemplatesRequest>({
          api: Api.NODE,
          url: '/blockmanagement/scheduled-visit-templates',
          params: {},
        });

      yield* put(
        doEngageScheduledVisitTemplatesSet({
          key,
          templates: response.messages,
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
      yield* put(
        doEngageScheduledVisitTemplatesSet({
          key,
          templates: [],
        }),
      );
    }
  }),

  takeEvery(
    doEngageScheduledVisitCommunicationScheduleLoad,
    function* f(action) {
      const { key, propertyUid, scheduledVisitId } = action.payload;
      try {
        const response =
          yield* requestGet<BlockManagerGetScheduledVisitCommunicationScheduleRequest>(
            {
              api: Api.NODE,
              url: '/blockmanagement/properties/:propertyUid/scheduled-visits/:scheduledVisitId/communication-schedule',
              params: { propertyUid, scheduledVisitId },
            },
            {
              abortKey: `scheduledVisitCommunicationScheduleLoad-${key}`,
            },
          );

        yield* put(
          doEngageScheduledVisitCommunicationScheduleSet({
            key,
            communicationSchedule: response.scheduledVisitCommunicationSchedule,
            scheduledVisitId,
          }),
        );
      } catch (error) {
        if (isError(error) && (!isNetworkError(error) || !error.isCancelled)) {
          yield* put(ALERT_ERROR({ error }));
        }
      }
    },
  ),

  takeEvery(
    doEngageScheduledVisitCommunicationScheduleUnload,
    function* f(action) {
      yield* apiAbort(
        `scheduledVisitCommunicationScheduleLoad-${action.payload.key}`,
      );
    },
  ),

  takeEvery(doEngageOpenInspectorSelector, function* f(action) {
    const { key, propertyId, inspectorIds } = action.payload;
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response =
        yield* requestGet<BlockManagerGetPropertyAccountsRequest>({
          api: Api.NODE,
          url: '/blockmanagement/properties/:propertyUid/accounts',
          params: { propertyUid: propertyId },
        });
      yield* put(HIDE_BLOCKING_LOADER());
      yield* sagaSelect({
        selectConfig: {
          title: 'Select Inspectors',
          value: inspectorIds,
          searchable: true,
          multiSelect: true,
          submitOnSelection: false,
          options: response.accounts.map((account) => ({
            name: `${account.forename} ${account.surname}`,
            labelAlignTop: true,
            value: `${account.email}`,
            internalValue: account.id,
          })),
        },
        onSelect: function* (values) {
          const stringValues: string[] = values.filter(
            (v) => typeof v === 'string',
          );

          const eventResponseAccounts = response.accounts.filter((account) =>
            stringValues.includes(account.id),
          );

          yield* put(
            eventTypeInspectorsSelected({
              eventKey: key,
              accounts: eventResponseAccounts,
            }),
          );

          return true;
        },
      });
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
    }
  }),
];
